import { useEffect, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { ChangedElsewhereNotice } from '../../components/ChangedElsewhereNotice'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { CityTypeahead } from '../../components/CityTypeahead'
import { FormCheckbox, FormField, FormSelect } from '../../components/FormField'
import { RecordLink } from '../../components/RecordLink'
import { RecordPicker } from '../../components/RecordPicker'
import { describeBookRecord } from '../../components/bookRecord'
import { apiErrorMessage } from '../../lib/errors'
import { CHANGED_ELSEWHERE } from '../../lib/formErrors'
import { formatBusinessDate, formatMoney } from '../../lib/format'
import { keepDecimal, parseAmount } from '../../lib/number'
import { listAgents, type BrokerAgent } from '../agents/api'
import { LOAD_TYPES, SECUREMENT_GEAR, type LoadType, type SecurementGear } from '../availability/api'
import { searchActiveBrokers, type BrokerSummary } from '../brokers/api'
import {
  EQUIPMENT_TYPES,
  featuresAllowedOn,
  isOpenDeck,
  type EquipmentFeature,
  type EquipmentType,
} from '../carriers/api'
import { routeLine } from '../matching/describe'
import {
  cancelLoad,
  discardLoads,
  captureLoad,
  getLoad,
  LOAD_SOURCES,
  MAX_STOPS,
  updateLoad,
  type Load,
  type LoadPayload,
  type LoadSource,
  type StopType,
} from './api'
import { loadMessageFor, stopField, toLoadFormErrors, withoutLoadErrorsFor, type FormErrors } from './errors'
import { LoadStandingBadge } from './LoadStandingBadge'

interface StopForm {
  /** What React tells stops apart by across inserts and removals; never sent. */
  key: string
  type: StopType
  city: string
  state: string
  company: string
  street: string
  windowFrom: string
  windowTo: string
  referenceNumber: string
  notes: string
}

/**
 * The broker a load is filed under, as the form holds it: enough to name it in the picker and
 * to warn about it — from the book's answer when chosen, from the load's own view when read.
 */
type LoadBroker = Pick<BrokerSummary, 'id' | 'name' | 'complianceState' | 'active'>

interface LoadForm {
  source: LoadSource
  broker: LoadBroker | null
  /**
   * The company offering the load, while no broker from the book is named (TRKC-104): Hugo
   * registers a broker when he books one of its loads, never to capture one.
   */
  company: string
  agentId: string
  rate: string
  tripMiles: string
  equipment: string
  alternativeEquipment: string
  datEquipmentCode: string
  lengthFeet: string
  weight: string
  loadType: LoadType
  pallets: string
  commodity: string
  reeferMin: string
  reeferMax: string
  hazmat: boolean
  /** What the unit must have (TRKC-100). */
  features: EquipmentFeature[]
  /** What a deck must carry to tie the load down (TRKC-100). */
  securement: SecurementGear[]
  datReference: string
  notes: string
  stops: StopForm[]
}

const noErrors: FormErrors = { fields: {}, summary: [] }

let lastStopKey = 0

function emptyStop(type: StopType): StopForm {
  lastStopKey += 1
  return {
    key: `stop-${lastStopKey}`,
    type,
    city: '',
    state: '',
    company: '',
    street: '',
    windowFrom: '',
    windowTo: '',
    referenceNumber: '',
    notes: '',
  }
}

function emptyForm(): LoadForm {
  return {
    source: 'Dat',
    broker: null,
    company: '',
    agentId: '',
    rate: '',
    tripMiles: '',
    equipment: '',
    alternativeEquipment: '',
    datEquipmentCode: '',
    lengthFeet: '',
    weight: '',
    loadType: 'FTL',
    pallets: '',
    commodity: '',
    reeferMin: '',
    reeferMax: '',
    hazmat: false,
    features: [],
    securement: [],
    datReference: '',
    notes: '',
    stops: [emptyStop('Pickup'), emptyStop('Delivery')],
  }
}

const text = (value: number | null): string => value?.toString() ?? ''

/** A load's saved values, laid into the form's boxes. */
function formOf(load: Load): LoadForm {
  return {
    source: load.source,
    // Named from the load itself, never looked up in a list: a broker past the first 500 of
    // the book read "Choose a broker" here, without its Blocked warning, while a save still
    // sent its id (A1). A load filed under the company's name names nobody from the book.
    broker:
      load.brokerId === null || load.brokerComplianceState === null
        ? null
        : {
            id: load.brokerId,
            name: load.brokerName,
            complianceState: load.brokerComplianceState,
            active: load.brokerActive,
          },
    company: load.brokerId === null ? load.brokerName : '',
    agentId: load.agentId ?? '',
    rate: text(load.rateUsd),
    tripMiles: text(load.tripMiles),
    equipment: load.equipment,
    alternativeEquipment: load.alternativeEquipment ?? '',
    datEquipmentCode: load.datEquipmentCode ?? '',
    lengthFeet: text(load.lengthFeet),
    weight: load.weightPounds.toString(),
    loadType: load.loadType,
    pallets: text(load.palletCount),
    commodity: load.commodity ?? '',
    reeferMin: text(load.reeferMinFahrenheit),
    reeferMax: text(load.reeferMaxFahrenheit),
    hazmat: load.hazmat,
    features: load.features,
    securement: load.securement,
    datReference: load.datReference ?? '',
    notes: load.notes ?? '',
    stops: load.stops.map((stop) => ({
      ...emptyStop(stop.type),
      city: stop.city,
      state: stop.state,
      company: stop.company ?? '',
      street: stop.street ?? '',
      windowFrom: stop.windowFrom ?? '',
      windowTo: stop.windowTo ?? '',
      referenceNumber: stop.referenceNumber ?? '',
      notes: stop.notes ?? '',
    })),
  }
}

const blankToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim())

/** A stop's complaints are keyed by its position, so moving stops makes every one of them stale. */
function withoutStopErrors(errors: FormErrors): FormErrors {
  const fields = Object.fromEntries(
    Object.entries(errors.fields).filter(([field]) => !field.startsWith('stops')),
  )
  return Object.keys(fields).length === Object.keys(errors.fields).length ? errors : { ...errors, fields }
}

/**
 * Captures a load, or edits one. One component for `/loads/new` and `/loads/:id`, the
 * posting form's shape: capture lands on the load's own page, where the same form edits
 * it and the load can be cancelled.
 *
 * <p>The boxes run in the order of DAT's row (Lino, 2026-09-26) — rate, trip, the route
 * and its days, equipment, length, weight, capacity, broker, contact — so a load read off
 * the board is typed left to right; what DAT does not show follows. The broker comes from
 * the book when the operation works with it; until one of its loads is booked, the load is
 * filed under the company's name as offered (TRKC-104).</p>
 */
export function LoadFormPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id: loadId } = useParams()
  const isEditing = loadId !== undefined

  const [agents, setAgents] = useState<BrokerAgent[] | null>(null)
  const [load, setLoad] = useState<Load | null>(null)
  const [form, setForm] = useState<LoadForm>(emptyForm)
  const [errors, setErrors] = useState<FormErrors>(noErrors)
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isDiscarding, setIsDiscarding] = useState(false)
  // The discard went through, and the load could not be read back: the page has closed all the
  // same, and offers to read who and when again (Copilot on #63).
  const [recordFailure, setRecordFailure] = useState<{ error: unknown } | null>(null)
  const [isLoadingSaved, setIsLoadingSaved] = useState(false)
  // Saving, cancelling and loading what is saved each answer with the whole load, and two
  // answers in flight can land in either order — a save answered after a cancellation
  // would show the load Open again. So the form runs one of them at a time (GPT-5.6 Sol
  // on #32).
  const isBusy = isSubmitting || isCancelling || isDiscarding || isLoadingSaved
  const [saved, setSaved] = useState(false)
  // The reading this form was opened on — set when the load arrives, by its own save and
  // by "load what is saved", never by anything else (see RecordVersion).
  const [version, setVersion] = useState<string | null>(null)

  // Opening the page: the load when editing one. Brokers are searched for in their picker.
  useEffect(() => {
    let active = true
    setIsLoading(true)
    setLoadFailure(null)

    void (async () => {
      try {
        const existing = loadId === undefined ? null : await getLoad(loadId)

        if (active) {
          setLoad(existing)
          setForm(existing === null ? emptyForm() : formOf(existing))
          setVersion(existing?.updatedAt ?? null)
        }
      } catch (caught) {
        if (active) {
          setLoadFailure({ error: caught })
        }
      } finally {
        if (active) {
          setIsLoading(false)
        }
      }
    })()

    return () => {
      active = false
    }
  }, [loadId])

  // The contacts follow the broker. Between two brokers the list is empty, never the
  // previous broker's: a contact picked from it would belong to somebody else (the
  // posting form's carrier picker learned this on #28).
  const brokerId = form.broker?.id ?? ''

  useEffect(() => {
    if (brokerId === '') {
      setAgents([])
      return
    }

    let active = true
    setAgents(null)

    listAgents(brokerId)
      .then((list) => {
        if (active) {
          setAgents(list)
        }
      })
      .catch(() => {
        if (active) {
          setAgents([])
        }
      })

    return () => {
      active = false
    }
  }, [brokerId])

  function set<K extends keyof LoadForm>(field: K, value: LoadForm[K], errorField: string = field) {
    setForm((current) => ({ ...current, [field]: value }))
    setErrors((current) => withoutLoadErrorsFor(current, errorField))
    setSaved(false)
  }

  function setStop<K extends keyof StopForm>(index: number, field: K, value: StopForm[K]) {
    setForm((current) => ({
      ...current,
      stops: current.stops.map((stop, position) => (position === index ? { ...stop, [field]: value } : stop)),
    }))
    setErrors((current) => withoutLoadErrorsFor(current, stopField(index, field)))
    setSaved(false)
  }

  function chooseBroker(broker: LoadBroker | null) {
    setForm((current) => ({ ...current, broker, agentId: '' }))
    setErrors((current) =>
      withoutLoadErrorsFor(withoutLoadErrorsFor(withoutLoadErrorsFor(current, 'brokerId'), 'agentId'), 'brokerCompany'),
    )
    setSaved(false)
  }

  /**
   * The equipment decides whether a temperature range can be stated: a range belongs to
   * a load that rides in a reefer, its own type or the one it also takes. Naming neither
   * empties the range rather than sending one the server would refuse.
   */
  function chooseEquipment(field: 'equipment' | 'alternativeEquipment', value: string) {
    setForm((current) => {
      const next = { ...current, [field]: value }
      const reefer = next.equipment === 'Reefer' || next.alternativeEquipment === 'Reefer'
      const withRange = reefer ? next : { ...next, reeferMin: '', reeferMax: '' }

      // What the types no longer allow goes with them (TRKC-100): a vent leaves with the van,
      // chains with the last open deck.
      const own = withRange.equipment as EquipmentType
      const also = withRange.alternativeEquipment as EquipmentType
      const allowed = featuresAllowedOn(own, also)

      return {
        ...withRange,
        features: withRange.features.filter((feature) => allowed.includes(feature)),
        securement: isOpenDeck(own) || isOpenDeck(also) ? withRange.securement : [],
      }
    })
    setErrors((current) => withoutLoadErrorsFor(current, field))
    setSaved(false)
  }

  /** A pickup joins the pickups, after the last of them; a delivery goes last. */
  function addStop(type: StopType) {
    setForm((current) => {
      const stops = [...current.stops]
      const lastPickup = stops.map((stop) => stop.type).lastIndexOf('Pickup')
      stops.splice(type === 'Pickup' ? lastPickup + 1 : stops.length, 0, emptyStop(type))
      return { ...current, stops }
    })
    setErrors((current) => withoutLoadErrorsFor(withoutStopErrors(current), 'stops'))
    setSaved(false)
  }

  function removeStop(index: number) {
    setForm((current) => ({ ...current, stops: current.stops.filter((_, position) => position !== index) }))
    setErrors((current) => withoutLoadErrorsFor(withoutStopErrors(current), 'stops'))
    setSaved(false)
  }

  async function loadWhatIsSaved() {
    if (loadId === undefined) {
      return
    }

    setIsLoadingSaved(true)

    try {
      const current = await getLoad(loadId)
      setLoad(current)
      setForm(formOf(current))
      setVersion(current.updatedAt)
      setErrors(noErrors)
    } catch (caught) {
      setErrors(toLoadFormErrors(caught))
    } finally {
      setIsLoadingSaved(false)
    }
  }

  async function cancel() {
    if (loadId === undefined) {
      return
    }

    setIsCancelling(true)

    try {
      // Closed now: its boxes show what the server holds, never a draft left unsaved
      // (GPT-6.1 Sol on #63, found on the discard and older here).
      showRecord(await cancelLoad(loadId))
      setErrors(noErrors)
    } catch (caught) {
      setErrors(toLoadFormErrors(caught))
    } finally {
      setIsCancelling(false)
    }
  }

  /**
   * Created by mistake (TRKC-110 item 4): asked first, because nothing brings it back. Once the
   * discard is answered the page closes, whatever follows; it then reads the load again to show
   * it as the record it stays — who discarded it, and when. A read that fails is said apart, with
   * a way to try again: the discard itself is done (Copilot on #63).
   */
  async function discard() {
    if (loadId === undefined || !window.confirm(t('loads.form.discardConfirm'))) {
      return
    }

    setIsDiscarding(true)

    try {
      await discardLoads([loadId])
    } catch (caught) {
      setErrors(toLoadFormErrors(caught))
      setIsDiscarding(false)
      return
    }

    // Until the record is read back, the boxes show the last version read — never a draft
    // left unsaved, which would read as the record itself (GPT-6.1 Sol on #63).
    if (load !== null) {
      showRecord({ ...load, standing: 'Discarded' })
    }
    setErrors(noErrors)
    await readRecord()
    setIsDiscarding(false)
  }

  /** A closed load as the record it is: its boxes and its version follow what was read. */
  function showRecord(record: Load) {
    setLoad(record)
    setForm(formOf(record))
    setVersion(record.updatedAt)
  }

  /** The discarded load as the record it stays: who discarded it, and when. */
  async function readRecord() {
    if (loadId === undefined) {
      return
    }

    try {
      showRecord(await getLoad(loadId))
      setRecordFailure(null)
    } catch (caught) {
      setRecordFailure({ error: caught })
    }
  }

  /**
   * A box left blank is a value nobody gave; a box holding something the platform cannot
   * read — "2..5", a paste long enough to overflow a double, or a fraction where the API
   * counts whole ones — is a value that would be dropped or refused on the way out, an
   * optional one in silence (GPT-5.6 Sol on #28). Every numeric box is asked, with the code
   * the server itself answers for that box, so the sentence says what to type either way.
   */
  function unreadableNumbers(reeferShown: boolean): Record<string, string> {
    const unreadable: Record<string, string> = {}
    const boxes: ReadonlyArray<readonly [string, string, string, boolean]> = [
      ['rate', form.rate, 'load_rate_invalid', false],
      ['tripMiles', form.tripMiles, 'load_trip_miles_invalid', true],
      ['lengthFeet', form.lengthFeet, 'load_length_invalid', true],
      ['weight', form.weight, 'weight_out_of_range', false],
      ['pallets', form.pallets, 'pallet_capacity_out_of_range', true],
      // The range is complained about on its lowest box, as the server's own code does.
      ...(reeferShown
        ? ([
            ['reeferMin', form.reeferMin, 'load_reefer_range_invalid', true],
            ['reeferMin', form.reeferMax, 'load_reefer_range_invalid', true],
          ] as const)
        : []),
    ]

    for (const [field, value, code, whole] of boxes) {
      const parsed = parseAmount(value)

      if (value.trim() !== '' && (parsed === null || (whole && !Number.isInteger(parsed)))) {
        unreadable[field] = code
      }
    }

    return unreadable
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrors(noErrors)
    setSaved(false)

    const reeferShown = form.equipment === 'Reefer' || form.alternativeEquipment === 'Reefer'
    const unreadable = unreadableNumbers(reeferShown)

    if (Object.keys(unreadable).length > 0) {
      setErrors({ fields: unreadable, summary: [] })
      return
    }

    const payload: LoadPayload = {
      source: form.source,
      brokerId: form.broker?.id ?? null,
      // The broker from the book is the truth once named; the company's name counts only without one.
      brokerCompany: form.broker === null ? blankToNull(form.company) : null,
      agentId: blankToNull(form.agentId),
      equipment: blankToNull(form.equipment) as EquipmentType | null,
      alternativeEquipment: blankToNull(form.alternativeEquipment) as EquipmentType | null,
      datEquipmentCode: blankToNull(form.datEquipmentCode),
      loadType: form.loadType,
      weightPounds: parseAmount(form.weight),
      lengthFeet: parseAmount(form.lengthFeet),
      palletCount: parseAmount(form.pallets),
      tripMiles: parseAmount(form.tripMiles),
      reeferMinFahrenheit: reeferShown ? parseAmount(form.reeferMin) : null,
      reeferMaxFahrenheit: reeferShown ? parseAmount(form.reeferMax) : null,
      hazmat: form.hazmat,
      features: form.features,
      securement: form.securement,
      rateUsd: parseAmount(form.rate),
      commodity: blankToNull(form.commodity),
      datReference: blankToNull(form.datReference),
      notes: blankToNull(form.notes),
      stops: form.stops.map((stop) => ({
        type: stop.type,
        city: stop.city.trim(),
        state: stop.state.trim(),
        company: blankToNull(stop.company),
        street: blankToNull(stop.street),
        windowFrom: blankToNull(stop.windowFrom),
        windowTo: blankToNull(stop.windowTo),
        referenceNumber: blankToNull(stop.referenceNumber),
        notes: blankToNull(stop.notes),
      })),
      version: version ?? undefined,
    }

    setIsSubmitting(true)

    try {
      if (loadId === undefined) {
        const created = await captureLoad(payload)
        void navigate(`/loads/${created.id}`, { replace: true })
        return
      }

      const updated = await updateLoad(loadId, payload)
      setLoad(updated)
      setForm(formOf(updated))
      setVersion(updated.updatedAt)
      setSaved(true)
    } catch (caught) {
      setErrors(toLoadFormErrors(caught))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return <p className="session-check">{t('loads.form.loading')}</p>
  }

  if (loadFailure !== null) {
    return (
      <section className="page">
        <Breadcrumbs
          trail={[
            { label: t('nav.loads'), to: '/loads' },
            { label: isEditing ? t('loads.form.editTitle') : t('loads.form.captureTitle') },
          ]}
        />
        <header className="page__header">
          <h1>{isEditing ? t('loads.form.editTitle') : t('loads.form.captureTitle')}</h1>
        </header>
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'loads.form.loadError')}
        </p>
        <p>
          <Link to="/loads">{t('loads.form.backToBook')}</Link>
        </p>
      </section>
    )
  }

  const field = (name: string): ReactNode => {
    const code = errors.fields[name]

    if (code === undefined) {
      return undefined
    }

    const subject = errors.subjects?.[code]
    const sentence = loadMessageFor(code, t, subject)

    return subject === undefined ? (
      sentence
    ) : (
      <>
        {sentence} <RecordLink subject={subject} />
      </>
    )
  }

  const fieldText = (name: string): string | undefined => {
    const code = errors.fields[name]
    return code === undefined ? undefined : loadMessageFor(code, t, errors.subjects?.[code])
  }

  const cancelled = load?.standing === 'Cancelled'
  const discarded = load?.standing === 'Discarded'
  // Neither takes an edit: a cancelled load is history, a discarded one a record.
  const closed = cancelled || discarded
  const reeferShown = form.equipment === 'Reefer' || form.alternativeEquipment === 'Reefer'
  const pickups = form.stops.filter((stop) => stop.type === 'Pickup').length
  const featuresAllowed = featuresAllowedOn(
    form.equipment as EquipmentType,
    form.alternativeEquipment as EquipmentType,
  )
  const openDeck =
    isOpenDeck(form.equipment as EquipmentType) || isOpenDeck(form.alternativeEquipment as EquipmentType)
  const deliveries = form.stops.length - pickups

  // A contact we stopped working with is not offered for a new choice — but the one this
  // load already names stays listed, said as such, so the form shows the truth instead of
  // an empty box. (The broker's picker searches only the brokers in play, and names the
  // load's own the same way.)
  const agentOptions = (agents ?? [])
    .filter((agent) => agent.active || agent.id === load?.agentId)
    .map((agent) => {
      const name = `${agent.firstName} ${agent.lastName}`
      return {
        value: agent.id,
        label: agent.active
          ? t('loads.form.contactOption', { name, email: agent.email })
          : t('loads.form.inactiveOption', { name }),
      }
    })

  return (
    <section className="page">
      <Breadcrumbs
        trail={[
          { label: t('nav.loads'), to: '/loads' },
          { label: isEditing ? (load === null ? null : routeLine(load, t)) : t('breadcrumbs.newLoad') },
        ]}
      />
      <header className="page__header page__header--row">
        <div>
          <h1>{isEditing ? t('loads.form.editTitle') : t('loads.form.captureTitle')}</h1>
          {load !== null && (
            <p className="page__subtitle">
              <LoadStandingBadge standing={load.standing} /> {load.brokerName}
              {load.rateUsd !== null && ` · ${formatMoney(load.rateUsd, 'USD', i18n.language)}`}
            </p>
          )}
        </div>
        <div className="page__actions">
          <Link className="button button--quiet" to="/loads">
            {t('loads.form.backToBook')}
          </Link>
          {load !== null && load.standing === 'Open' && (
            <Link className="button" to={`/loads/${load.id}/matches`}>
              {t('matching.openUnits')}
            </Link>
          )}
        </div>
      </header>

      {saved && (
        <p className="page__subtitle" role="status">
          {t('loads.form.saved')}
        </p>
      )}

      {load !== null && load.brokerId !== null && !load.brokerActive && !closed && (
        <p className="alert alert--warning" role="status">
          {t('loads.form.brokerInactiveNote', { name: load.brokerName })}{' '}
          <RecordLink subject={{ kind: 'Broker', id: load.brokerId, name: load.brokerName }} />
        </p>
      )}

      {discarded && (
        <p className="alert alert--warning" role="status">
          {load?.discardedAt == null
            ? t('loads.form.discardedJustNow')
            : load.discardedByEmail == null
              ? t('loads.form.discardedNote', { when: formatBusinessDate(load.discardedAt, i18n.language) })
              : t('loads.form.discardedByNote', {
                  when: formatBusinessDate(load.discardedAt, i18n.language),
                  who: load.discardedByEmail,
                })}
        </p>
      )}

      {recordFailure !== null && (
        <div className="alert" role="alert">
          <p>{apiErrorMessage(recordFailure.error, t, 'loads.form.recordError')}</p>
          <button type="button" className="button button--quiet" onClick={() => void readRecord()}>
            {t('loads.form.readRecordAgain')}
          </button>
        </div>
      )}

      {cancelled && (
        <p className="alert alert--warning" role="status">
          {t('loads.form.cancelledNote', {
            // The day the cancellation fell on in the business zone, not in UTC.
            when: load?.cancelledAt == null ? '' : formatBusinessDate(load.cancelledAt, i18n.language),
          })}
        </p>
      )}

      {errors.summary.includes(CHANGED_ELSEWHERE) ? (
        <ChangedElsewhereNotice
          onLoadSaved={() => void loadWhatIsSaved()}
          isLoading={isLoadingSaved}
          disabled={isSubmitting || isCancelling || isDiscarding}
        />
      ) : (
        errors.summary.length > 0 && (
          <div className="alert" role="alert">
            {errors.summary.map((code) => (
              <p key={code}>{loadMessageFor(code, t)}</p>
            ))}
          </div>
        )
      )}

      <form className="form form--stacked" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <fieldset className="fieldset" disabled={closed}>
          <legend>{t('loads.form.rateSection')}</legend>

          <FormField
            name="rate"
            label={t('loads.form.rate')}
            value={form.rate}
            onChange={(value) => set('rate', keepDecimal(form.rate, value))}
            error={field('rate')}
            inputMode="decimal"
            width="narrow"
          />
          <FormField
            name="tripMiles"
            label={t('loads.form.tripMiles')}
            value={form.tripMiles}
            onChange={(value) => set('tripMiles', keepDecimal(form.tripMiles, value))}
            error={field('tripMiles')}
            inputMode="numeric"
            width="narrow"
          />
        </fieldset>

        <fieldset className="fieldset" disabled={closed}>
          <legend>{t('loads.form.routeSection')}</legend>

          {errors.fields.stops !== undefined && (
            <p className="alert loads__stops-error" role="alert">
              {loadMessageFor(errors.fields.stops, t)}
            </p>
          )}

          {form.stops.map((stop, index) => {
            const position = index + 1
            const removable = form.stops.length > 2 && (stop.type === 'Pickup' ? pickups > 1 : deliveries > 1)

            return (
              <fieldset key={stop.key} className="loads__stop">
                <legend>{t('loads.form.stopLegend', { position, type: t(`loads.form.stopTypes.${stop.type}`) })}</legend>

                <CityTypeahead
                  name={`stop-${index}-city`}
                  label={t('loads.form.city')}
                  value={stop.city}
                  onChange={(value) => setStop(index, 'city', value)}
                  onSelect={({ city, state }) => {
                    setStop(index, 'city', city)
                    setStop(index, 'state', state)
                  }}
                  error={fieldText(stopField(index, 'city'))}
                  maxLength={100}
                  required
                  preferredState={stop.state}
                />
                <FormField
                  name={`stop-${index}-state`}
                  label={t('loads.form.state')}
                  value={stop.state}
                  onChange={(value) => setStop(index, 'state', value.toUpperCase())}
                  error={field(stopField(index, 'state'))}
                  maxLength={2}
                  placeholder="FL"
                  required
                  width="narrow"
                />
                <FormField
                  name={`stop-${index}-windowFrom`}
                  label={t('loads.form.windowFrom')}
                  type="date"
                  value={stop.windowFrom}
                  onChange={(value) => setStop(index, 'windowFrom', value)}
                  error={field(stopField(index, 'windowFrom'))}
                  required={index === 0}
                />
                <FormField
                  name={`stop-${index}-windowTo`}
                  label={t('loads.form.windowTo')}
                  type="date"
                  value={stop.windowTo}
                  onChange={(value) => setStop(index, 'windowTo', value)}
                  placeholder={t('loads.form.windowHint')}
                />
                <FormField
                  name={`stop-${index}-company`}
                  label={t('loads.form.company')}
                  value={stop.company}
                  onChange={(value) => setStop(index, 'company', value)}
                  error={field(stopField(index, 'company'))}
                  maxLength={100}
                />
                <FormField
                  name={`stop-${index}-street`}
                  label={t('loads.form.street')}
                  value={stop.street}
                  onChange={(value) => setStop(index, 'street', value)}
                  error={field(stopField(index, 'street'))}
                  maxLength={200}
                />
                <FormField
                  name={`stop-${index}-referenceNumber`}
                  label={t('loads.form.referenceNumber')}
                  value={stop.referenceNumber}
                  onChange={(value) => setStop(index, 'referenceNumber', value)}
                  error={field(stopField(index, 'referenceNumber'))}
                  maxLength={50}
                />
                <FormField
                  name={`stop-${index}-notes`}
                  label={t('loads.form.stopNotes')}
                  value={stop.notes}
                  onChange={(value) => setStop(index, 'notes', value)}
                  error={field(stopField(index, 'notes'))}
                  maxLength={500}
                  width="wide"
                />

                {removable && (
                  <div className="loads__stop-actions">
                    <button type="button" className="button button--quiet" onClick={() => removeStop(index)}>
                      {t('loads.form.removeStop', { position })}
                    </button>
                  </div>
                )}
              </fieldset>
            )
          })}

          {form.stops.length < MAX_STOPS && (
            <div className="loads__stop-actions">
              <button type="button" className="button button--quiet" onClick={() => addStop('Pickup')}>
                {t('loads.form.addPickup')}
              </button>
              <button type="button" className="button button--quiet" onClick={() => addStop('Delivery')}>
                {t('loads.form.addDelivery')}
              </button>
            </div>
          )}
        </fieldset>

        <fieldset className="fieldset" disabled={closed}>
          <legend>{t('loads.form.freightSection')}</legend>

          <FormSelect
            name="equipment"
            label={t('loads.form.equipment')}
            value={form.equipment}
            onChange={(value) => chooseEquipment('equipment', value)}
            options={[
              { value: '', label: t('loads.form.chooseEquipment') },
              ...EQUIPMENT_TYPES.map((type) => ({ value: type, label: t(`carriers.types.${type}`) })),
            ]}
            error={field('equipment')}
            required
          />
          <FormSelect
            name="alternativeEquipment"
            label={t('loads.form.alternativeEquipment')}
            value={form.alternativeEquipment}
            onChange={(value) => chooseEquipment('alternativeEquipment', value)}
            options={[
              { value: '', label: t('loads.form.noAlternative') },
              ...EQUIPMENT_TYPES.filter((type) => type !== form.equipment).map((type) => ({
                value: type,
                label: t(`carriers.types.${type}`),
              })),
            ]}
            error={field('alternativeEquipment')}
          />
          <FormField
            name="datEquipmentCode"
            label={t('loads.form.datEquipmentCode')}
            value={form.datEquipmentCode}
            onChange={(value) => set('datEquipmentCode', value.toUpperCase())}
            error={field('datEquipmentCode')}
            maxLength={4}
            placeholder="VR"
            width="narrow"
          />
          <FormField
            name="lengthFeet"
            label={t('loads.form.lengthFeet')}
            value={form.lengthFeet}
            onChange={(value) => set('lengthFeet', keepDecimal(form.lengthFeet, value))}
            error={field('lengthFeet')}
            inputMode="numeric"
            width="narrow"
          />
          <FormField
            name="weight"
            label={t('loads.form.weight')}
            value={form.weight}
            onChange={(value) => set('weight', keepDecimal(form.weight, value))}
            error={field('weight')}
            inputMode="decimal"
            required
            width="narrow"
          />
          <FormSelect
            name="loadType"
            label={t('loads.form.loadType')}
            value={form.loadType}
            onChange={(value) => set('loadType', value as LoadType)}
            options={LOAD_TYPES.map((type) => ({ value: type, label: t(`availability.loadTypes.${type}`) }))}
            error={field('loadType')}
            required
          />
          <FormField
            name="pallets"
            label={t('loads.form.pallets')}
            value={form.pallets}
            onChange={(value) => set('pallets', keepDecimal(form.pallets, value))}
            error={field('pallets')}
            inputMode="numeric"
            width="narrow"
          />
          <FormField
            name="commodity"
            label={t('loads.form.commodity')}
            value={form.commodity}
            onChange={(value) => set('commodity', value)}
            error={field('commodity')}
            maxLength={100}
          />

          {/* A load that rides in a reefer states its temperatures (TRKC-110); one that only
              also takes a reefer (VR) may, and need not — on a van they bind nothing. */}
          {reeferShown && (
            <>
              <FormField
                name="reeferMin"
                label={t('loads.form.reeferMin')}
                value={form.reeferMin}
                onChange={(value) => set('reeferMin', keepDecimal(form.reeferMin, value))}
                error={field('reeferMin')}
                inputMode="numeric"
                required={form.equipment === 'Reefer'}
                width="narrow"
              />
              <FormField
                name="reeferMax"
                label={t('loads.form.reeferMax')}
                value={form.reeferMax}
                onChange={(value) => set('reeferMax', keepDecimal(form.reeferMax, value), 'reeferMax')}
                inputMode="numeric"
                required={form.equipment === 'Reefer'}
                width="narrow"
              />
            </>
          )}

          <FormCheckbox
            name="hazmat"
            label={t('loads.form.hazmat')}
            checked={form.hazmat}
            onChange={(checked) => set('hazmat', checked)}
          />

          {/* Only what the load's types can have is offered (TRKC-100); the server judges every save. */}
          {featuresAllowed.length > 0 && (
            <div className="field field--choices" role="group" aria-labelledby="load-features-label">
              <span id="load-features-label">{t('loads.form.features')}</span>
              {featuresAllowed.map((feature) => (
                <FormCheckbox
                  key={feature}
                  name={`feature-${feature}`}
                  label={t(`equipment.features.${feature}`)}
                  checked={form.features.includes(feature)}
                  onChange={(checked) =>
                    set(
                      'features',
                      checked ? [...form.features, feature] : form.features.filter((existing) => existing !== feature),
                    )
                  }
                />
              ))}
              {field('features') !== undefined && (
                <span className="field__error" role="alert">
                  {field('features')}
                </span>
              )}
            </div>
          )}

          {openDeck && (
            <div className="field field--choices" role="group" aria-labelledby="load-securement-label">
              <span id="load-securement-label">{t('loads.form.securement')}</span>
              {SECUREMENT_GEAR.map((gear) => (
                <FormCheckbox
                  key={gear}
                  name={`securement-${gear}`}
                  label={t(`availability.gear.${gear}`)}
                  checked={form.securement.includes(gear)}
                  onChange={(checked) =>
                    set(
                      'securement',
                      checked ? [...form.securement, gear] : form.securement.filter((existing) => existing !== gear),
                    )
                  }
                />
              ))}
              {field('securement') !== undefined && (
                <span className="field__error" role="alert">
                  {field('securement')}
                </span>
              )}
            </div>
          )}
        </fieldset>

        <fieldset className="fieldset" disabled={closed}>
          <legend>{t('loads.form.brokerSection')}</legend>

          <RecordPicker<LoadBroker>
            name="brokerId"
            label={t('loads.form.broker')}
            value={form.broker}
            onChange={chooseBroker}
            search={searchActiveBrokers}
            describe={(record) => describeBookRecord(record, t)}
            textOf={(record) => (record.active ? record.name : t('loads.form.inactiveOption', { name: record.name }))}
            placeholder={t('picker.placeholder')}
            error={field('brokerId')}
          />
          {/* Until a broker from the book is named, the load is filed under the company's
              name — the broker is registered when one of its loads is booked (TRKC-104). */}
          {form.broker === null && (
            <FormField
              name="brokerCompany"
              label={t('loads.form.brokerCompany')}
              value={form.company}
              onChange={(value) => set('company', value, 'brokerCompany')}
              error={field('brokerCompany')}
              maxLength={200}
              required
            />
          )}
          <FormSelect
            name="agentId"
            label={t('loads.form.contact')}
            value={form.agentId}
            onChange={(value) => set('agentId', value)}
            options={[{ value: '', label: t('loads.form.noContact') }, ...agentOptions]}
            error={field('agentId')}
            disabled={agents === null || form.broker === null}
          />
          {form.broker?.complianceState === 'Blocked' && (
            <p className="loads__broker-help">{t('loads.form.brokerBlocked', { name: form.broker.name })}</p>
          )}
          {/* Registering stays possible, never asked for: every search asks the book as it
              stands, so a broker registered in the other tab is found as soon as it is saved. */}
          {form.broker === null && (
            <p className="loads__broker-help">
              {t('loads.form.notInBook')}{' '}
              <Link to="/brokers/new" target="_blank" rel="noreferrer">
                {t('loads.form.registerBroker')}
              </Link>
            </p>
          )}

          {isEditing ? (
            <p className="field">
              <span>{t('loads.form.source')}</span>
              <strong>{t(`loads.form.sources.${form.source}`)}</strong>
            </p>
          ) : (
            <FormSelect
              name="source"
              label={t('loads.form.source')}
              value={form.source}
              onChange={(value) => set('source', value as LoadSource)}
              options={LOAD_SOURCES.map((source) => ({ value: source, label: t(`loads.form.sources.${source}`) }))}
              error={field('source')}
              required
            />
          )}
          {/* Who DAT listed for the load, as it listed them — kept from the paste, never
              typed here: the contact to name from the book is the one above. */}
          {load !== null && load.contacts.length > 0 && (
            <div className="field field--wide">
              <span>{t('loads.form.contacts')}</span>
              <ul className="loads__contacts" aria-label={t('loads.form.contacts')}>
                {load.contacts.map((contact) => (
                  <li key={`${contact.email ?? ''}|${contact.phone ?? ''}`}>
                    {[contact.email, contact.phone].filter((part) => part !== null).join(' · ')}
                  </li>
                ))}
              </ul>
              <span className="field__hint">{t('loads.form.contactsHint')}</span>
            </div>
          )}
          <FormField
            name="datReference"
            label={t('loads.form.datReference')}
            value={form.datReference}
            onChange={(value) => set('datReference', value)}
            error={field('datReference')}
            maxLength={50}
          />
          <FormField
            name="notes"
            label={t('loads.form.notes')}
            value={form.notes}
            onChange={(value) => set('notes', value)}
            error={field('notes')}
            maxLength={1000}
            multiline
            rows={3}
            width="wide"
          />
        </fieldset>

        <div className="form__actions">
          {/* A mistake is a mistake whether the broker pulled the load or not. */}
          {isEditing && load !== null && !discarded && (
            <button type="button" className="button button--quiet" onClick={() => void discard()} disabled={isBusy}>
              {isDiscarding ? t('loads.form.discarding') : t('loads.form.discard')}
            </button>
          )}
          {isEditing && !closed && (
            <button type="button" className="button button--quiet" onClick={() => void cancel()} disabled={isBusy}>
              {t('loads.form.cancelLoad')}
            </button>
          )}
          {!closed && (
            <button type="submit" className="button" disabled={isBusy}>
              {isSubmitting ? t('loads.form.saving') : isEditing ? t('loads.form.save') : t('loads.form.capture')}
            </button>
          )}
        </div>
      </form>
    </section>
  )
}
